import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FolderHeart, ArrowLeft, Image, Video, Loader2 } from "lucide-react";
import TopHeader from "@/components/TopHeader";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UnlockedContentDisplay } from "@/components/content/UnlockedContentDisplay";
import {
  useOwnedContent,
  type OwnedContentItem,
  type BotWithOwnedContent,
} from "@/hooks/use-owned-content";
import supabase from "@/supabase";
import { cn } from "@/lib/utils";

interface GalleryItemProps {
  item: OwnedContentItem;
  onView: (item: OwnedContentItem, signedUrl: string) => void;
}

function GalleryItem({ item, onView }: GalleryItemProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Generate signed URL for thumbnail on mount
  useEffect(() => {
    let cancelled = false;

    const loadThumbnail = async () => {
      const { data, error: signedUrlError } = await supabase.storage
        .from("bot-media")
        .createSignedUrl(item.thumbnail_path, 3600);

      if (cancelled) return;

      if (signedUrlError || !data) {
        console.error("Failed to load thumbnail:", signedUrlError);
        setError(true);
        setIsLoading(false);
        return;
      }

      setThumbnailUrl(data.signedUrl);
      setIsLoading(false);
    };

    loadThumbnail();

    return () => {
      cancelled = true;
    };
  }, [item.thumbnail_path]);

  const handleClick = useCallback(async () => {
    // Generate signed URL for full content
    const { data, error } = await supabase.storage
      .from("bot-media")
      .createSignedUrl(item.storage_path, 3600);

    if (error || !data) {
      console.error("Failed to generate content URL:", error);
      return;
    }

    onView(item, data.signedUrl);
  }, [item, onView]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative aspect-square rounded-lg overflow-hidden bg-muted group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      data-testid={`gallery-item-${item.id}`}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Image className="h-8 w-8 text-muted-foreground" />
        </div>
      )}

      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={item.title || "Owned content"}
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
          onLoad={() => setIsLoading(false)}
          onError={() => setError(true)}
        />
      )}

      {/* Media type indicator */}
      <div className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5">
        {item.type === "video" ? (
          <Video className="h-3.5 w-3.5 text-white" />
        ) : (
          <Image className="h-3.5 w-3.5 text-white" />
        )}
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
    </button>
  );
}

interface BotSectionProps {
  bot: BotWithOwnedContent;
  onViewItem: (item: OwnedContentItem, signedUrl: string) => void;
}

function BotSection({ bot, onViewItem }: BotSectionProps) {
  return (
    <div className="mb-8" data-testid={`bot-section-${bot.id}`}>
      {/* Bot header */}
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="h-10 w-10">
          <AvatarImage src={bot.avatar_url || undefined} alt={bot.name} />
          <AvatarFallback>{bot.name.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <h2 className="font-semibold text-foreground">{bot.name}</h2>
          <p className="text-sm text-muted-foreground">
            {bot.items.length} {bot.items.length === 1 ? "item" : "items"}
          </p>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-3 gap-2">
        {bot.items.map((item) => (
          <GalleryItem key={item.id} item={item} onView={onViewItem} />
        ))}
      </div>
    </div>
  );
}

export default function OwnedContentGalleryPage() {
  const navigate = useNavigate();
  const { botGroups, totalItems, isLoading, error, refetch } = useOwnedContent();
  const [selectedItem, setSelectedItem] = useState<{
    item: OwnedContentItem;
    signedUrl: string;
  } | null>(null);

  const handleViewItem = useCallback(
    (item: OwnedContentItem, signedUrl: string) => {
      setSelectedItem({ item, signedUrl });
    },
    []
  );

  const handleCloseViewer = useCallback(() => {
    setSelectedItem(null);
  }, []);

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <TopHeader />

      <main className="flex-1 overflow-y-auto pb-20">
        <div className="p-4">
          {/* Page header with back button */}
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <FolderHeart className="h-6 w-6 text-primary" />
              <h1 className="text-xl font-bold">My Content</h1>
            </div>
          </div>

          <p className="text-muted-foreground mb-6">
            View all the content you&apos;ve purchased, organized by companion.
          </p>

          {/* Loading state */}
          {isLoading && (
            <div
              className="flex items-center justify-center py-12"
              data-testid="loading-state"
            >
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div
              className="text-center py-12"
              data-testid="error-state"
            >
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={refetch} variant="outline">
                Try Again
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && totalItems === 0 && (
            <div
              className={cn(
                "flex flex-col items-center justify-center py-12 text-center"
              )}
              data-testid="empty-state"
            >
              <FolderHeart className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h2 className="text-lg font-medium text-foreground mb-2">
                No content yet
              </h2>
              <p className="text-muted-foreground max-w-[280px] mb-4">
                Content you purchase from AI companions will appear here.
              </p>
              <Button onClick={() => navigate("/bots")} data-testid="browse-bots-button">
                Browse AI Companions
              </Button>
            </div>
          )}

          {/* Content grouped by bot */}
          {!isLoading && !error && totalItems > 0 && (
            <div data-testid="content-gallery">
              {/* Total count */}
              <p className="text-sm text-muted-foreground mb-4">
                {totalItems} {totalItems === 1 ? "item" : "items"} from{" "}
                {botGroups.length}{" "}
                {botGroups.length === 1 ? "companion" : "companions"}
              </p>

              {botGroups.map((bot) => (
                <BotSection
                  key={bot.id}
                  bot={bot}
                  onViewItem={handleViewItem}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t p-2 flex justify-around items-center text-gray-600 shadow-lg">
        <BottomNav />
      </footer>

      {/* Full-screen media viewer */}
      {selectedItem && (
        <UnlockedContentDisplay
          contentId={selectedItem.item.id}
          contentType={selectedItem.item.type === "video" ? "media" : "media"}
          mediaUrl={selectedItem.signedUrl}
          title={selectedItem.item.title}
          isOpen={true}
          onClose={handleCloseViewer}
        />
      )}
    </div>
  );
}
